import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { extractErrorMessage } from '../../core/services/api';
import {
  llmAgent,
  resources,
  type LlmJobState,
  type LlmJobStatus,
} from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import type {
  LineOfBizCategory,
  LineOfBizPersona,
  Prompt,
  PromptFunnelStage,
  PromptIntentionType,
} from '../../core/types/data-models';

/**
 * Shape of `result` returned by `generatePromptAction`. Mirrors the
 * "Output format" section of the prompt template — the model returns
 * a list of `{prompt_text, description, category, persona, intention,
 * importance, funnel_stage, language}` objects under `prompts`.
 */
interface PromptSuggestion {
  prompt_text: string;
  description?: string;
  category?: string;
  persona?: string;
  intention?: string;
  importance?: string;
  funnel_stage?: string;
  language?: string;
}

interface PromptsResult {
  prompts?: PromptSuggestion[];
}

interface GeneratePromptsDialogProps {
  open: boolean;
  lineOfBizId: number | null;
  onClose: () => void;
}

const TERMINAL_STATES: ReadonlySet<LlmJobState> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

const ALL_INTENTIONS: PromptIntentionType[] = [
  'BRANDED',
  'UNBRANDED',
  'INFORMATIONAL',
  'COMPARISON',
];

/** Discrete prompt-count choices, mirroring the backend's allowed set. */
const PROMPT_COUNTS = [10, 50, 100] as const;
const DEFAULT_PROMPT_COUNT = 10;

/**
 * Multi-step dialog that drives the generate-prompts flow:
 *
 *   1. **Filter** — user sees four multi-select pickers (categories,
 *      personas, funnel stages, intention types) all pre-selected. They
 *      can narrow before clicking Generate.
 *   2. **Progress** — POST starts the job; we poll until terminal.
 *   3. **Review** — pie-chart stats per category / persona / intention,
 *      plus a collapsed list of every generated prompt with a
 *      checkbox (preselected). User unchecks any they don't want.
 *   4. **Save** — sequentially create `Prompt` rows for every
 *      selected suggestion. The natural-key `identifier` is derived
 *      server-side from a hash of the prompt text, so duplicates raise
 *      a unique-constraint violation we surface in an Alert.
 */
export function GeneratePromptsDialog({
  open,
  lineOfBizId,
  onClose,
}: GeneratePromptsDialogProps) {
  const queryClient = useQueryClient();
  const { activeTenantId } = useTenant();

  // ----- Catalogs for the filter step --------------------------------
  // Shared cache keys so we hit warm queries on most pages.
  const categoriesQuery = useQuery({
    queryKey: ['line-of-biz-categories', 'select'],
    queryFn: () => resources.lineOfBizCategories.list({ pageSize: 200, sort: 'name' }),
    enabled: open,
  });
  const personasQuery = useQuery({
    queryKey: ['line-of-biz-personas', 'select'],
    queryFn: () => resources.lineOfBizPersonas.list({ pageSize: 200, sort: 'name' }),
    enabled: open,
  });
  const stagesQuery = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 200, sort: 'name' }),
    enabled: open,
  });

  // LOB row — used to seed the Languages picker from
  // `supportedLanguages`. Cached under the same key the LOB dashboard
  // uses so the fetch is shared.
  const lobQuery = useQuery({
    queryKey: ['line-of-business', lineOfBizId],
    queryFn: () => resources.linesOfBusiness.get(lineOfBizId!),
    enabled: open && lineOfBizId != null,
  });
  const lobLanguages = useMemo(
    () => lobQuery.data?.supportedLanguages ?? [],
    [lobQuery.data?.supportedLanguages],
  );

  // LOB-scoped catalogs — only the rows for the active LOB are valid
  // picks for the filter step (the action queries by lineOfBizId).
  const categories = useMemo(
    () =>
      (categoriesQuery.data?.data ?? []).filter(
        (c) => lineOfBizId == null || c.lineOfBizId === lineOfBizId,
      ),
    [categoriesQuery.data, lineOfBizId],
  );
  const personas = useMemo(
    () =>
      (personasQuery.data?.data ?? []).filter(
        (p) => lineOfBizId == null || p.lineOfBizId === lineOfBizId,
      ),
    [personasQuery.data, lineOfBizId],
  );
  const funnelStages = stagesQuery.data?.data ?? [];

  // ----- Filter selections (default: every option selected) ----------
  const [selectedCategories, setSelectedCategories] = useState<LineOfBizCategory[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<LineOfBizPersona[]>([]);
  const [selectedStages, setSelectedStages] = useState<PromptFunnelStage[]>([]);
  const [selectedIntentions, setSelectedIntentions] =
    useState<PromptIntentionType[]>(ALL_INTENTIONS);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [promptCount, setPromptCount] = useState<number>(DEFAULT_PROMPT_COUNT);

  // Re-seed selections to "all" whenever the catalog finishes loading
  // or the dialog (re-)opens. We don't re-seed if the user has already
  // ticked anything off — that would clobber their work.
  useEffect(() => {
    if (open && selectedCategories.length === 0) setSelectedCategories(categories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categories.length]);
  useEffect(() => {
    if (open && selectedPersonas.length === 0) setSelectedPersonas(personas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, personas.length]);
  useEffect(() => {
    if (open && selectedStages.length === 0) setSelectedStages(funnelStages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, funnelStages.length]);
  // Seed languages from the LOB's `supportedLanguages` once the row
  // lands. Skip the seed if the user has already picked something so
  // we don't clobber their edits.
  useEffect(() => {
    if (open && selectedLanguages.length === 0 && lobLanguages.length > 0) {
      setSelectedLanguages(lobLanguages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lobLanguages.length]);

  // ----- Job state ---------------------------------------------------
  const [status, setStatus] = useState<LlmJobStatus<PromptsResult> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const jobIdRef = useRef<string | null>(null);

  // ----- Selected prompts on the review step -------------------------
  const [selectedPromptIdx, setSelectedPromptIdx] = useState<Set<number>>(new Set());

  // ----- Start job ----------------------------------------------------
  const start = useMutation({
    mutationFn: (args: {
      id: number;
      categoryIds?: number[];
      personaIds?: number[];
      funnelStageIds?: number[];
      intentions?: PromptIntentionType[];
      languages?: string[];
      count?: number;
    }) =>
      resources.generatePrompts(args.id, {
        categoryIds: args.categoryIds,
        personaIds: args.personaIds,
        funnelStageIds: args.funnelStageIds,
        intentions: args.intentions,
        languages: args.languages,
        count: args.count,
      }),
    onSuccess: (data) => {
      jobIdRef.current = data.jobId;
      setStatus(data.status as LlmJobStatus<PromptsResult>);
    },
    onError: (err) => setStartError(extractErrorMessage(err)),
  });

  const handleGenerate = () => {
    if (lineOfBizId == null) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelectedPromptIdx(new Set());
    jobIdRef.current = null;
    start.mutate({
      id: lineOfBizId,
      categoryIds: selectedCategories.map((c) => c.id),
      personaIds: selectedPersonas.map((p) => p.id),
      funnelStageIds: selectedStages.map((s) => s.id),
      intentions: selectedIntentions,
      languages: selectedLanguages,
      count: promptCount,
    });
  };

  // ----- Reset on open ------------------------------------------------
  useEffect(() => {
    if (!open) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelectedPromptIdx(new Set());
    jobIdRef.current = null;
    setSelectedIntentions(ALL_INTENTIONS);
    setSelectedLanguages([]);
    setPromptCount(DEFAULT_PROMPT_COUNT);
    // We deliberately do NOT clear selectedCategories/Personas/Stages
    // here — the catalog effects above seed them once they load.
  }, [open, lineOfBizId]);

  // ----- Polling loop ------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (status == null) return;
    if (TERMINAL_STATES.has(status.state)) return;
    let cancelled = false;
    const tick = async () => {
      const jobId = jobIdRef.current;
      if (!jobId) return;
      try {
        const next = await llmAgent.getJob<PromptsResult>(jobId);
        if (!cancelled) setStatus(next);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    };
    const handle = window.setInterval(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, status]);

  // Preselect every generated prompt on completion.
  useEffect(() => {
    if (status?.state !== 'completed') return;
    const items = status.result?.prompts ?? [];
    setSelectedPromptIdx(new Set(items.map((_, idx) => idx)));
  }, [status]);

  const prompts = status?.result?.prompts ?? [];
  const lastStep = status?.steps?.[status.steps.length - 1]?.step;
  const isRunning =
    start.isPending || (status != null && !TERMINAL_STATES.has(status.state));
  const isCompleted = status?.state === 'completed';

  // ----- Save selected prompts ---------------------------------------
  // Resolve string labels back to FK ids using the catalogs. Names
  // are matched case-insensitively. Unmatched labels leave the FK
  // null — the user can fix them up by editing the row afterwards.
  const categoryIdByName = useMemo(() => indexByLowerName(categories), [categories]);
  const personaIdByName = useMemo(() => indexByLowerName(personas), [personas]);
  const stageIdByName = useMemo(() => indexByLowerName(funnelStages), [funnelStages]);

  const save = useMutation({
    mutationFn: async (picks: PromptSuggestion[]) => {
      if (lineOfBizId == null || activeTenantId == null) return;
      for (const p of picks) {
        const text = p.prompt_text?.trim();
        if (!text) continue;
        const intention = normaliseIntention(p.intention);
        const importance = normaliseImportance(p.importance);
        await resources.prompts.create({
          lineOfBizId,
          tenantId: activeTenantId,
          prompt: text,
          language: (p.language || 'en').slice(0, 5),
          intentionType: intention ?? 'INFORMATIONAL',
          defaultImportance: importance ?? 'MEDIUM',
          defaultCategoryId: lookupId(categoryIdByName, p.category),
          defaultPersonaId: lookupId(personaIdByName, p.persona),
          defaultStageId: lookupId(stageIdByName, p.funnel_stage),
          defaultBrandId: null,
        } as Partial<Prompt>);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const canSave = isCompleted && selectedPromptIdx.size > 0 && !save.isPending;
  const canGenerate =
    !isRunning
    && !isCompleted
    && lineOfBizId != null
    && selectedCategories.length > 0
    && selectedPersonas.length > 0
    && selectedStages.length > 0
    && selectedIntentions.length > 0
    && selectedLanguages.length > 0;

  // ----- Submit dispatcher --------------------------------------------
  // The dialog has two "primary" actions across its lifecycle: Generate
  // (filter step) and Save (review step). We hand FormDialog the
  // right label + handler for the current step.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCompleted) {
      if (!canSave) return;
      setError(null);
      const picks = prompts.filter((_, idx) => selectedPromptIdx.has(idx));
      await save.mutateAsync(picks);
      return;
    }
    if (!isRunning && canGenerate) {
      handleGenerate();
    }
  };

  const submitLabel = isCompleted
    ? save.isPending
      ? 'Saving…'
      : `Save ${selectedPromptIdx.size} prompt${selectedPromptIdx.size === 1 ? '' : 's'}`
    : 'Generate';
  const submitDisabled = isCompleted ? !canSave : !canGenerate || isRunning;

  return (
    <FormDialog
      open={open}
      title="Generate prompts"
      submitLabel={submitLabel}
      submitDisabled={submitDisabled}
      loading={save.isPending || isRunning}
      maxWidth="lg"
      onSubmit={handleSubmit}
      onClose={() => {
        if (save.isPending) return;
        onClose();
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {/* ---- Filter step (visible until we have a result) ---- */}
      {!isCompleted && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Pick which categories, personas, funnel stages and intention types
            the LLM should consider. Every option is selected by default.
          </Typography>

          <FilterAutocomplete
            label="Categories"
            options={categories}
            value={selectedCategories}
            onChange={setSelectedCategories}
            loading={categoriesQuery.isLoading}
          />
          <FilterAutocomplete
            label="Personas"
            options={personas}
            value={selectedPersonas}
            onChange={setSelectedPersonas}
            loading={personasQuery.isLoading}
          />
          <FilterAutocomplete
            label="Funnel stages"
            options={funnelStages}
            value={selectedStages}
            onChange={setSelectedStages}
            loading={stagesQuery.isLoading}
          />
          <Autocomplete
            multiple
            options={ALL_INTENTIONS}
            value={selectedIntentions}
            onChange={(_, next) => setSelectedIntentions(next)}
            isOptionEqualToValue={(a, b) => a === b}
            renderInput={(p) => (
              <TextField {...p} label="Intention types" placeholder="Any" />
            )}
          />
          {/* Languages — pulled from `LineOfBiz.supportedLanguages` so
              the LOB owner controls the catalog. freeSolo to let the
              user type an extra code (e.g. 'nl') without first adding
              it to the LOB record. */}
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            options={lobLanguages}
            value={selectedLanguages}
            onChange={(_, next) =>
              setSelectedLanguages(
                next
                  .map((v) => v.trim().toLowerCase())
                  .filter((v) => v.length > 0),
              )
            }
            isOptionEqualToValue={(a, b) => a === b}
            loading={lobQuery.isLoading}
            disableCloseOnSelect
            renderInput={(p) => (
              <TextField
                {...p}
                label="Languages"
                placeholder={
                  lobLanguages.length === 0
                    ? 'No supported languages set on this LOB — type ISO codes to add'
                    : 'Any'
                }
                helperText="ISO codes from the LOB's supported languages."
              />
            )}
          />
          {/* How many prompts the LLM should generate in total. */}
          <TextField
            select
            label="Number of prompts"
            value={promptCount}
            onChange={(e) => setPromptCount(Number(e.target.value))}
            sx={{ maxWidth: 240 }}
          >
            {PROMPT_COUNTS.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      {/* ---- Job progress ---- */}
      {isRunning && (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {status?.state === 'pending' || status == null
                ? 'Starting…'
                : lastStep ?? 'Researching buyer prompts…'}
            </Typography>
          </Stack>
          <LinearProgress
            variant={
              status?.progress != null && status.progress > 0
                ? 'determinate'
                : 'indeterminate'
            }
            value={
              status?.progress != null
                ? Math.round(status.progress * 100)
                : undefined
            }
          />
        </Stack>
      )}

      {status?.state === 'failed' && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={handleGenerate}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {status.error || 'The prompt-research job failed.'}
        </Alert>
      )}
      {status?.state === 'cancelled' && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={handleGenerate}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          The prompt-research job was cancelled.
        </Alert>
      )}
      {startError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={handleGenerate}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {startError}
        </Alert>
      )}

      {/* ---- Results review ---- */}
      {isCompleted && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {prompts.length === 0 ? (
            <Alert severity="warning">The analyzer returned no prompts.</Alert>
          ) : (
            <>
              {/* Statistics — three pie charts side-by-side. */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                useFlexGap
                flexWrap="wrap"
              >
                <StatsCard
                  title="By category"
                  counts={countBy(prompts, (p) => p.category)}
                />
                <StatsCard
                  title="By persona"
                  counts={countBy(prompts, (p) => p.persona)}
                />
                <StatsCard
                  title="By intention"
                  counts={countBy(prompts, (p) => p.intention)}
                />
              </Stack>

              <Divider />

              {/* Collapsed by default — each row a checkbox + the
                  prompt text + a small chip cluster (intention,
                  importance, category/persona/stage). */}
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Prompts ({selectedPromptIdx.size}/{prompts.length} selected)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {prompts.map((p, idx) => (
                      <Paper
                        key={`${p.prompt_text.slice(0, 50)}-${idx}`}
                        variant="outlined"
                        sx={{ p: 1.25 }}
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                          <Checkbox
                            checked={selectedPromptIdx.has(idx)}
                            onChange={() => togglePromptIdx(idx, setSelectedPromptIdx)}
                            sx={{ p: 0.5, mt: -0.25 }}
                          />
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {p.prompt_text}
                            </Typography>
                            {p.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25 }}
                              >
                                {p.description}
                              </Typography>
                            )}
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ mt: 0.75 }}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {p.intention && (
                                <Chip size="small" label={p.intention} variant="outlined" />
                              )}
                              {p.importance && (
                                <Chip size="small" label={p.importance} variant="outlined" />
                              )}
                              {p.category && (
                                <Chip size="small" label={`Cat: ${p.category}`} variant="outlined" />
                              )}
                              {p.persona && (
                                <Chip size="small" label={`Persona: ${p.persona}`} variant="outlined" />
                              )}
                              {p.funnel_stage && (
                                <Chip size="small" label={`Stage: ${p.funnel_stage}`} variant="outlined" />
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Stack>
      )}
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function togglePromptIdx(
  idx: number,
  setSelected: React.Dispatch<React.SetStateAction<Set<number>>>,
) {
  setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    return next;
  });
}

function indexByLowerName<T extends { id: number; name: string }>(
  rows: T[],
): Map<string, number> {
  const map = new Map<string, number>();
  for (const r of rows) map.set(r.name.trim().toLowerCase(), r.id);
  return map;
}

function lookupId(
  index: Map<string, number>,
  label?: string,
): number | null {
  if (!label) return null;
  return index.get(label.trim().toLowerCase()) ?? null;
}

function normaliseIntention(value: string | undefined): PromptIntentionType | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  if (ALL_INTENTIONS.includes(upper as PromptIntentionType)) {
    return upper as PromptIntentionType;
  }
  return null;
}

function normaliseImportance(value: string | undefined): 'LOW' | 'MEDIUM' | 'HIGH' | null {
  if (!value) return null;
  const upper = value.trim().toUpperCase();
  if (upper === 'LOW' || upper === 'MEDIUM' || upper === 'HIGH') return upper;
  return null;
}

function countBy<T>(items: T[], key: (item: T) => string | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const item of items) {
    const k = key(item)?.trim() || '(unspecified)';
    out.set(k, (out.get(k) ?? 0) + 1);
  }
  return out;
}

/**
 * Multi-select autocomplete that turns named rows on/off. Behaves
 * like the catalog filters on the report-template page — same chip
 * collapsing + checkbox renderOption pattern.
 */
function FilterAutocomplete<T extends { id: number; name: string }>({
  label,
  options,
  value,
  onChange,
  loading,
}: {
  label: string;
  options: T[];
  value: T[];
  onChange: (next: T[]) => void;
  loading?: boolean;
}) {
  return (
    <Autocomplete<T, true, false, false>
      multiple
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={loading}
      disableCloseOnSelect
      limitTags={4}
      renderInput={(p) => <TextField {...p} label={label} placeholder="Any" />}
    />
  );
}

/**
 * Compact card with a CSS conic-gradient "pie" + name/count legend.
 * Avoids pulling in a charting library for what's effectively three
 * tiny bar charts. Slice colours come from a deterministic palette
 * keyed on the slice label so repeated dialog openings stay stable.
 */
function StatsCard({ title, counts }: { title: string; counts: Map<string, number> }) {
  const entries = useMemo(() => {
    const total = [...counts.values()].reduce((sum, n) => sum + n, 0);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({
        label,
        count,
        share: total === 0 ? 0 : count / total,
        color: colorForLabel(label),
      }));
  }, [counts]);

  // Build the conic-gradient stops by accumulating shares around the
  // ring. Each slice gets a `0 → cumulative` band painted in its
  // colour; the next slice starts where the previous one stopped.
  const gradient = useMemo(() => {
    if (entries.length === 0) return 'conic-gradient(#e0e0e0 0 100%)';
    let acc = 0;
    const stops = entries.map(({ color, share }) => {
      const start = (acc * 100).toFixed(2);
      acc += share;
      const end = (acc * 100).toFixed(2);
      return `${color} ${start}% ${end}%`;
    });
    return `conic-gradient(${stops.join(', ')})`;
  }, [entries]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 220px', minWidth: 220 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: '50%',
            background: gradient,
            // Donut hole — keeps it readable when there are many small
            // slices.
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 18,
              borderRadius: '50%',
              backgroundColor: 'background.paper',
            },
          }}
        />
        <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
          {entries.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              No data
            </Typography>
          ) : (
            entries.map((e) => (
              <Stack
                key={e.label}
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '2px',
                    backgroundColor: e.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                  {e.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {e.count}
                </Typography>
              </Stack>
            ))
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

// Small palette + deterministic label → colour mapping. Hashing keeps
// the same category always rendering in the same colour across re-opens.
const PALETTE = [
  '#1976d2', '#9c27b0', '#2e7d32', '#ef6c00', '#0288d1',
  '#c62828', '#5e35b1', '#00897b', '#fbc02d', '#6d4c41',
];

function colorForLabel(label: string): string {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

